/**
 * ч.1312: «документ, відкритий за посиланням, не знайдено».
 *
 * Редактори документів на 404 лишались порожньою формою — з межами записів «лише свої»
 * (ч.1312) таке трапляється частіше: колега надіслав посилання на свій документ. Замість
 * правки кожного редактора — одне місце: кеш запитів (`main.tsx`) повідомляє сюди про 404
 * на GET, а макет (`AppLayout`) показує плашку, якщо ідентифікатор із запиту є в адресі
 * сторінки (тобто не знайдено саме відкритий документ, а не щось допоміжне).
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const EVT = 'oblik:doc-not-found';

/** Ідентифікатори з адреси запиту, які є і в адресі сторінки. */
export function missingIdsOnPage(requestUrl: string, pathname: string): string[] {
  const path = pathname.toLowerCase();
  const fromRequest = (requestUrl.split('?')[0] ?? '').match(UUID_RE) ?? [];
  return fromRequest.map((s) => s.toLowerCase()).filter((id) => path.includes(id));
}

/** Викликає кеш запитів на помилку запиту. */
export function reportQueryError(error: unknown): void {
  const e = error as { response?: { status?: number }; config?: { url?: string; method?: string } };
  if (e?.response?.status !== 404) return;
  if ((e.config?.method ?? 'get').toLowerCase() !== 'get') return;
  const ids = missingIdsOnPage(e.config?.url ?? '', window.location.pathname);
  if (ids.length === 0) return;
  window.dispatchEvent(new CustomEvent(EVT, { detail: { path: window.location.pathname } }));
}

/** Чи не знайдено документ поточної сторінки (скидається при переході). */
export function useDocNotFound(): boolean {
  const { pathname } = useLocation();
  const [missingOn, setMissingOn] = useState<string | null>(null);
  useEffect(() => {
    const on = (ev: Event) => setMissingOn((ev as CustomEvent<{ path: string }>).detail.path);
    window.addEventListener(EVT, on);
    return () => window.removeEventListener(EVT, on);
  }, []);
  return missingOn === pathname;
}
